import tinycolor from 'tinycolor2';
import { selectContrastingColor } from '../../utils/selectContrastingColor';

/**
 * Color scale generation functions for the ComputedUserTheme component.
 */

/**
 * Generate a scale of colors based on the brand primary color.
 *
 * - The brand primary scale is composed of 7 colors.
 *   - 3 dark colors, 3 light colors, and the base.
 * - The base color is converted to HSL, which allows us to keep the same hue and saturation, but
 *   step the lightness value up and down.
 * - A logarithmic scale is used to increase the step size as you move away from the base color.
 *   - This was done to help the edge colors (darkest and lightest) to be darker/lighter than they
 *     would have been with a linear scale.
 * - Also calculates the contrast color for each color in the scale.
 *
 * Note, expects a hex color string because that is how Rich Mahogany stores the theme colors.
 */
export function generateBrandPrimaryColorScale(hex) {
  const hsl = tinycolor(hex).toHsl();
  const hue = hsl.h;
  const saturation = hsl.s * 100; // Convert to percentage
  const baseLightness = hsl.l * 100; // Convert to percentage
  const toBrandPrimaryHsl = l => `hsl(${hue.toFixed(1)}, ${saturation.toFixed(1)}%, ${l.toFixed(1)}%)`;

  // The number of steps on each side of the base color
  const numSteps = 3;

  // The color CSS variables, ordered like [darkest, ..., base, ..., lightest]
  const colors = [];

  /**
   * Generate the dark colors.
   *
   * The value of darkLogBase is not magical. It was chosen after design fiddled
   * with different values.
   */
  const darkLogBase = 4.5;
  for (let i = numSteps; i >= 1; i -= 1) {
    const darkValue = baseLightness - Math.log(i + 1) / Math.log(darkLogBase) * (baseLightness / Math.log(numSteps + 1));
    colors.push(toBrandPrimaryHsl(Math.max(darkValue, 0)));
  }

  // Add the base color
  colors.push(toBrandPrimaryHsl(baseLightness));

  /**
   * Generate the light colors.
   *
   * The value of lightLogBase is not magical. It was chosen after design fiddled
   * with different values.
   */
  const lightLogBase = 3.3;
  for (let i = 1; i <= numSteps; i += 1) {
    const lightValue = baseLightness + Math.log(i + 1) / Math.log(lightLogBase) * ((100 - baseLightness) / Math.log(numSteps + 1));
    colors.push(toBrandPrimaryHsl(Math.min(lightValue, 100)));
  }

  // Generate the CSS variables for both the colors and their contrast colors.
  const cssVariables = ['darkest', 'darker', 'dark', 'base', 'light', 'lighter', 'lightest'].reduce((memo, k, i) => ({
    ...memo,
    [`--computed-color__brand-primary--${k}`]: colors[i],
    [`--computed-color__brand-primary--${k}-contrast`]: selectContrastingColor(colors[i])
  }), {});
  return cssVariables;
}

/**
 * Generate a scale of colors based on the brand secondary color.
 *
 * This scale only has one variation, and that is deliberate.
 */
export function generateBrandSecondaryColorScale(hex) {
  const hsl = tinycolor(hex).toHsl();
  const hue = hsl.h;
  const saturation = hsl.s * 100; // Convert to percentage
  const baseLightness = hsl.l * 100; // Convert to percentage
  const toBrandPrimaryHsl = l => `hsl(${hue.toFixed(1)}, ${saturation.toFixed(1)}%, ${l.toFixed(1)}%)`;
  const colors = [
  /**
   * There is no magic to the value of 7. It was chosen to match what was already in Figma.
   * Design tested out a slew of colors and was happy with the results.
   */
  toBrandPrimaryHsl(baseLightness - 7),
  // dark
  toBrandPrimaryHsl(baseLightness) // base
  ];

  // Generate the CSS variables for both the colors and their contrast colors.
  const cssVariables = ['dark', 'base'].reduce((memo, k, i) => ({
    ...memo,
    [`--computed-color__brand-secondary--${k}`]: colors[i],
    [`--computed-color__brand-secondary--${k}-contrast`]: selectContrastingColor(colors[i])
  }), {});
  return cssVariables;
}